import {
  get,
  set,
  push,
  update,
  remove,
  runTransaction,
  serverTimestamp,
  invoicesRef,
  invoiceRef,
  invoiceSettingRef,
  snapshotToArray,
  snapshotToItem,
} from '@/firebase/database';
import type { Invoice, InvoiceFormData, InvoiceStatus, InvoiceItem, TaxType } from '@/types';
import { calculateItemTotals, calculateInvoiceTotals } from '@/utils/invoiceCalculations';
import { getCustomerById } from './customerService';
import { createNotification } from './notificationService';
import { v4 as uuidv4 } from 'uuid';

export const getInvoices = async (companyId: string): Promise<Invoice[]> => {
  const snap = await get(invoicesRef(companyId));
  const list = snapshotToArray<Invoice>(snap, [
    'createdAt', 'updatedAt', 'invoiceDate', 'dueDate', 'finalizedAt', 'cancelledAt', 'pdfGeneratedAt', 'emailSentAt'
  ]);
  return list.map(inv => {
    const rawItems = inv.items || [];
    const itemsList: any[] = Array.isArray(rawItems) ? rawItems : Object.values(rawItems);
    const items = itemsList.map((it: any) => ({
      ...it,
      unit: it?.unit || 'PCS',
    }));
    return {
      ...inv,
      items,
      customerName: inv.customerName || 'Customer',
      invoiceNumber: inv.invoiceNumber || 'INV-0000',
    };
  }).sort((a, b) => {
    const timeA = a.createdAt?.valueOf ? a.createdAt.valueOf() : new Date(a.createdAt || 0).getTime();
    const timeB = b.createdAt?.valueOf ? b.createdAt.valueOf() : new Date(b.createdAt || 0).getTime();
    return timeB - timeA;
  });
};

export const getInvoiceById = async (companyId: string, id: string): Promise<Invoice | null> => {
  const snap = await get(invoiceRef(companyId, id));
  if (!snap.exists()) return null;
  const inv = snapshotToItem<Invoice>(snap, [
    'createdAt', 'updatedAt', 'invoiceDate', 'dueDate', 'finalizedAt', 'cancelledAt', 'pdfGeneratedAt', 'emailSentAt'
  ]);
  if (!inv) return null;
  const rawItems = inv.items || [];
  const itemsList: any[] = Array.isArray(rawItems) ? rawItems : Object.values(rawItems);
  const items = itemsList.map((it: any) => ({
    ...it,
    unit: it?.unit || 'PCS',
  }));
  return {
    ...inv,
    items,
    customerName: inv.customerName || 'Customer',
  };
};


export const getInvoicesByCustomer = async (companyId: string, customerId: string): Promise<Invoice[]> => {
  const all = await getInvoices(companyId);
  return all.filter(inv => inv.customerId === customerId);
};

export const generateInvoiceNumber = async (companyId: string): Promise<string> => {
  const settingRef = invoiceSettingRef(companyId);
  let invoiceNumber = '';

  const result = await runTransaction(settingRef, (currentData) => {
    const data = currentData || {};
    const prefix = data.invoicePrefix || 'INV';
    const current = typeof data.currentNumber === 'number' ? data.currentNumber : (data.startingNumber || 1000);
    const next = current + 1;
    invoiceNumber = `${prefix}-${next}`;
    return {
      ...data,
      companyId,
      invoicePrefix: prefix,
      startingNumber: data.startingNumber || 1000,
      currentNumber: next,
      updatedAt: Date.now(),
    };
  });

  if (result.committed && invoiceNumber) {
    return invoiceNumber;
  }

  // Fallback if transaction couldn't capture variable directly
  const snap = await get(settingRef);
  const data = snap.val() || {};
  const prefix = data.invoicePrefix || 'INV';
  const current = data.currentNumber || 1001;
  return `${prefix}-${current}`;
};

export const buildInvoiceItems = (
  formItems: InvoiceFormData['items'],
  taxType: TaxType,
): InvoiceItem[] => {
  return formItems.map(item => {
    const totals = calculateItemTotals(item.quantity, item.unitPrice, item.gstPercentage, taxType);
    return {
      id:            item.id || uuidv4(),
      productId:     item.productId || undefined,
      description:   item.description,
      quantity:      item.quantity,
      unit:          item.unit,
      unitPrice:     item.unitPrice,
      gstPercentage: item.gstPercentage,
      hsnCode:       item.hsnCode,
      ...totals,
    };
  });
};

export const saveInvoiceDraft = async (
  companyId: string,
  formData: InvoiceFormData,
  createdBy: string,
  existingId?: string,
): Promise<string> => {
  const items  = buildInvoiceItems(formData.items, formData.taxType);
  const totals = calculateInvoiceTotals(items, formData.discountType, formData.discountValue, formData.taxType);

  let customer = null;
  if (formData.customerId) {
    try {
      customer = await getCustomerById(companyId, formData.customerId);
    } catch (e) {
      console.warn('Could not fetch customer details:', e);
    }
  }

  const invoiceData = {
    companyId,
    customerId:        formData.customerId,
    customerName:      customer?.name || 'Customer',
    customerMobile:    customer?.mobile || '',
    customerEmail:     customer?.email || '',
    customerAddress:   customer?.address || '',
    customerCity:      customer?.city || '',
    customerState:     customer?.state || '',
    customerGstin:     customer?.gstin || '',
    invoiceDate:       new Date(formData.invoiceDate).getTime(),
    dueDate:           new Date(formData.dueDate).getTime(),
    paymentTerms:      formData.paymentTerms,
    items,
    discountType:      formData.discountType,
    discountValue:     formData.discountValue,
    taxType:           formData.taxType,
    billingType:       formData.billingType || 'retail',
    notes:             formData.notes || '',
    terms:             formData.terms || '',
    templateId:        formData.templateId,
    ...totals,
    paidAmount:        0,
    outstandingAmount: totals.grandTotal,
    status:            'draft' as InvoiceStatus,
    updatedBy:         createdBy,
    updatedAt:         serverTimestamp(),
  };


  if (existingId) {
    await update(invoiceRef(companyId, existingId), invoiceData);
    createNotification({
      companyId,
      category: 'invoice',
      type: 'invoice_updated',
      title: 'Invoice Updated',
      message: `Invoice for ${invoiceData.customerName} (₹${totals.grandTotal.toLocaleString('en-IN')}) was updated`,
      link: `/invoices/${existingId}`,
      userId: createdBy,
    }).catch(() => {});
    return existingId;
  } else {
    const invoiceNumber = await generateInvoiceNumber(companyId);
    const newRef = push(invoicesRef(companyId));
    const id = newRef.key as string;
    await set(newRef, {
      ...invoiceData,
      id,
      invoiceNumber,
      createdBy,
      createdAt: serverTimestamp(),
    });
    createNotification({
      companyId,
      category: 'invoice',
      type: 'invoice_created',
      title: 'New Invoice Created',
      message: `${invoiceNumber} generated for ${invoiceData.customerName} (₹${totals.grandTotal.toLocaleString('en-IN')})`,
      link: `/invoices/${id}`,
      userId: createdBy,
    }).catch(() => {});
    return id;
  }
};

export const finalizeInvoice = async (
  companyId: string,
  invoiceId: string,
  finalizedBy: string
): Promise<void> => {
  await update(invoiceRef(companyId, invoiceId), {
    status:      'finalized',
    finalizedAt: serverTimestamp(),
    finalizedBy,
    updatedAt:   serverTimestamp(),
  });
  createNotification({
    companyId,
    category: 'invoice',
    type: 'invoice_finalized',
    title: 'Invoice Finalized',
    message: `Invoice has been approved and finalized`,
    link: `/invoices/${invoiceId}`,
    userId: finalizedBy,
  }).catch(() => {});
};

export const cancelInvoice = async (
  companyId: string,
  invoiceId: string,
  reason: string
): Promise<void> => {
  await update(invoiceRef(companyId, invoiceId), {
    status:          'cancelled',
    cancelledAt:     serverTimestamp(),
    cancelledReason: reason,
    updatedAt:       serverTimestamp(),
  });
  createNotification({
    companyId,
    category: 'invoice',
    type: 'invoice_cancelled',
    title: 'Invoice Cancelled',
    message: reason ? `Reason: ${reason}` : `Invoice was cancelled`,
    link: `/invoices/${invoiceId}`,
  }).catch(() => {});
};

export const updatePaymentStatus = async (
  companyId: string,
  invoiceId: string,
  paidAmount: number,
  grandTotal: number,
): Promise<void> => {
  const outstandingAmount = Math.max(0, grandTotal - paidAmount);
  let status: InvoiceStatus = 'finalized';
  if (paidAmount >= grandTotal) {
    status = 'paid';
  } else if (paidAmount > 0) {
    status = 'partially_paid';
  }
  await update(invoiceRef(companyId, invoiceId), {
    paidAmount,
    outstandingAmount,
    status,
    updatedAt: serverTimestamp(),
  });
};

export const deleteInvoice = async (companyId: string, id: string): Promise<void> => {
  await remove(invoiceRef(companyId, id));
  createNotification({
    companyId,
    category: 'invoice',
    type: 'invoice_deleted',
    title: 'Invoice Deleted',
    message: `An invoice was removed from records`,
    link: '/invoices',
  }).catch(() => {});
};
